import keycloakClient from "@keycloak/keycloak";
import {
  AssignRoleToUserRequest,
  CreateRoleRequest,
  DeleteRoleRequest,
  UnassignRoleToUserRequest
} from "@services/accessManagerApi/apiRequestsModels";
import { ACCESS_MANAGER_API_URL } from "../../config";
import { Roles } from "./models/Roles";

class AccessRolesService {
  private baseUrl: string;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
  }

  private getAuthHeaders(includeContentType: boolean = true): HeadersInit {
    const headers: HeadersInit = {
      Authorization: `Bearer ${keycloakClient.token}`,
      Accept: "application/json"
    };
    if (includeContentType) {
      headers["Content-Type"] = "application/json";
    }
    return headers;
  }

  private handleApiError(error: any): never {
    console.error("Error en la comunicación con el API acces manager:", error);
    throw error;
  }

  async getAllRoles(): Promise<Roles> {
    try {
      const response = await fetch(`${this.baseUrl}/roles`, {
        method: "GET",
        headers: this.getAuthHeaders(false)
      });

      if (!response.ok) {
        throw new Error(
          `Error al obtener los roles: ${response.status} ${response.statusText}`
        );
      }

      return (await response.json()) as Roles;
    } catch (error) {
      this.handleApiError(error);
    }
  }

  async createNewRole(data: CreateRoleRequest): Promise<boolean> {
    try {
      const response = await fetch(`${this.baseUrl}/roles`, {
        method: "POST",
        headers: this.getAuthHeaders(true),
        body: JSON.stringify({
          name: data.name,
          description: data.description ?? ""
        })
      });

      if (!response.ok) {
        throw new Error(
          `Error al crear el rol: ${response.status} ${response.statusText}`
        );
      }
      return true;
    } catch (error) {
      this.handleApiError(error);
    }
  }

  async deleteRole(data: DeleteRoleRequest): Promise<boolean> {
    try {
      const response = await fetch(`${this.baseUrl}/roles`, {
        method: "DELETE",
        headers: this.getAuthHeaders(true),
        body: JSON.stringify({
          id: data.id
        })
      });

      if (!response.ok) {
        throw new Error(
          `Error al eliminar el rol: ${response.status} ${response.statusText}`
        );
      }
      return true;
    } catch (error) {
      this.handleApiError(error);
    }
  }

  async assignRole(data: AssignRoleToUserRequest): Promise<boolean> {
    try {
      const response = await fetch(`${this.baseUrl}/roles/assign`, {
        method: "POST",
        headers: this.getAuthHeaders(true),
        body: JSON.stringify({
          roles: data.roles
        })
      });

      if (!response.ok) {
        throw new Error(
          `Error assigning roles to user: ${response.status} ${response.statusText}`
        );
      }
      return true;
    } catch (error) {
      this.handleApiError(error);
    }
  }

  async unassignRole(data: UnassignRoleToUserRequest): Promise<boolean> {
    try {
      const response = await fetch(`${this.baseUrl}/roles/assign`, {
        method: "DELETE",
        headers: this.getAuthHeaders(true),
        body: JSON.stringify({
          roles: data.roles
        })
      });

      if (!response.ok) {
        throw new Error(
          `Error unassigning roles to user: ${response.status} ${response.statusText}`
        );
      }
      return true;
    } catch (error) {
      this.handleApiError(error);
    }
  }
}

export const accessRolesService = new AccessRolesService(
  `${ACCESS_MANAGER_API_URL}/chatbot-access-manager-api/v1`
);
